import { Keplr, Window as KeplrWindow } from "@keplr-wallet/types";
import { Psbt } from "bitcoinjs-lib";
import { Eip1193Provider } from "ethers";
import { Address, Msg, Network, Signer, Simulation, TxResult } from "rujira.js";
import { CosmosAdapter } from "./cosmos";
import { Eip6963Adapter } from "./eip6963";
import { walletType } from "./utils";
import { UtxoContext } from "./utxo";

interface BitcoinProvider {
  connect: () => Promise<{ address: string; publicKey: string }>;
  signPsbt: (
    psbtHex: string,
    options?: { autofinalized?: boolean }
  ) => Promise<string>;
}

declare global {
  interface Window extends KeplrWindow {
    okxwallet?: Eip1193Provider & {
      bitcoin: BitcoinProvider;
      keplr: Keplr & { isOkWallet: true };
    };
  }
}

export class OkxAdapter implements Signer {
  constructor(
    private e: Eip6963Adapter,
    private c: CosmosAdapter,
    private b?: BitcoinProvider
  ) {}
  connect(): Promise<Address[]> {
    if (!this.b) throw new Error("Okx extension not found");
    const accs = this.b.connect().then((res) => res.address);

    return Promise.all([this.e.connect(), this.c.connect(), accs]).then((x) =>
      x.flat()
    );
  }
  async simulate(msg: Msg): Promise<Simulation> {
    const type = walletType(msg.account.address);
    switch (type) {
      case "cosmos":
        return this.c.simulate(msg);
      case "evm":
        return this.e.simulate(msg);
      case "xrp":
      case "tron":
        throw new Error(`${type} not supported by CTRL Wallet`);

      default:
        const client = new UtxoContext(msg.account.address);
        const utxos = await client.fetchUtxos();
        const { fee } = await msg.toPsbt(utxos);

        return {
          symbol: "BTC",
          decimals: 8,
          amount: fee,
          gas: 0n,
        };
    }
  }
  async signAndBroadcast(simulation: Simulation, msg: Msg): Promise<TxResult> {
    const type = walletType(msg.account.address);
    switch (type) {
      case "cosmos":
        return this.c.signAndBroadcast(simulation, msg);
      case "evm":
        return this.e.signAndBroadcast(simulation, msg);
      case "xrp":
      case "tron":
        throw new Error(`${type} not supported by CTRL Wallet`);

      default:
        const client = new UtxoContext(msg.account.address);
        if (!this.b) throw new Error("Okx extension not found");
        const utxos = await client.fetchUtxos();
        const { psbt } = await msg.toPsbt(utxos);
        const res = await this.b.signPsbt(psbt.toHex(), {
          autofinalized: true,
        });
        const signed = Psbt.fromHex(res);
        return client.broadcast(
          msg.account.address,
          signed.extractTransaction(),
          msg.toDeposit?.()
        );
    }
  }
  onChange?: ((cb: () => void) => void) | undefined;
  isAvailable() {
    return this.e.isAvailable();
  }

  public networks(): Network[] {
    return ["BTC", ...this.c.networks(), ...this.e.networks()];
  }
}

const provider = new OkxAdapter(
  new Eip6963Adapter("com.okex.wallet"),
  new CosmosAdapter(() => window.okxwallet?.keplr),
  window.okxwallet?.bitcoin
);

export default provider;
